import { t } from '@lingui/core/macro'
import { Trans } from '@lingui/react/macro'
import { useEffect, useState } from 'react'
import ECharts from '~/components/echarts'
import {
  awsSummarySavingsOrganizationOptimizerAWSMasterOptimizationsPath,
  elasticacheCostAnalysisChartOrganizationOptimizerAWSMasterOptimizationsPath,
  elasticacheMemcachedRiChartOrganizationOptimizerAWSMasterOptimizationsPath,
  elasticacheNodeTypeChartsOrganizationOptimizerAWSMasterOptimizationsPath,
  elasticacheOptimizedNodeTypeChartsOrganizationOptimizerAWSMasterOptimizationsPath,
  elasticacheRedisRiChartOrganizationOptimizerAWSMasterOptimizationsPath,
} from '~/routes'
import { csrfHeader } from '~/utils/csrf'

export default function ElastiCacheOptimization({ organizationId, masterOptimizationId, masterOptimization }) {
  const [activeTab, setActiveTab] = useState('daily-cost')
  const [savingsData, setSavingsData] = useState(null)

  // biome-ignore lint/correctness/useExhaustiveDependencies: todo: re-evaluate dependencies
  useEffect(() => {
    if (masterOptimization) {
      fetchSavingsData()
    }
  }, [masterOptimization])

  const fetchSavingsData = async () => {
    try {
      const savingsUrl = awsSummarySavingsOrganizationOptimizerAWSMasterOptimizationsPath(organizationId)
      const response = await fetch(savingsUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...csrfHeader(),
        },
        body: JSON.stringify({ master_optimization_id: masterOptimizationId }),
      })

      if (response.ok) {
        const data = await response.json()
        setSavingsData(data)
      } else {
        console.error('Failed to fetch ElastiCache savings data:', response.status, response.statusText)
      }
    } catch (error) {
      console.error('Error fetching ElastiCache savings data:', error)
    }
  }

  if (!masterOptimization) {
    return null
  }

  const handleTabChange = (tabId) => {
    setActiveTab(tabId)
  }

  const dailySavings = savingsData?.elasticache_savings?.daily_savings || 0
  const savingsPercentage = savingsData?.elasticache_savings?.savings_percentage || 0

  return (
    <div className='row mb-4'>
      <div className='col-12'>
        <div className='card'>
          <div className='card-header'>
            <h5 className='card-title mb-0'>
              <Trans>
                ElastiCache Optimization - Daily Savings ${dailySavings.toFixed(2)} ({savingsPercentage.toFixed(1)}%)
              </Trans>
            </h5>
          </div>
          <div className='card-body'>
            <ul className='nav nav-tabs mb-3'>
              <li className='nav-item'>
                <button
                  className={`nav-link ${activeTab === 'daily-cost' ? 'active' : ''}`}
                  onClick={() => handleTabChange('daily-cost')}
                  type='button'
                >
                  <Trans>Daily Cost</Trans>
                </button>
              </li>
              <li className='nav-item'>
                <button
                  className={`nav-link ${activeTab === 'node-type' ? 'active' : ''}`}
                  onClick={() => handleTabChange('node-type')}
                  type='button'
                >
                  <Trans>Node Type</Trans>
                </button>
              </li>
              <li className='nav-item'>
                <button
                  className={`nav-link ${activeTab === 'redis-ris' ? 'active' : ''}`}
                  onClick={() => handleTabChange('redis-ris')}
                  type='button'
                >
                  <Trans>Redis RIs</Trans>
                </button>
              </li>
              <li className='nav-item'>
                <button
                  className={`nav-link ${activeTab === 'memcached-ris' ? 'active' : ''}`}
                  onClick={() => handleTabChange('memcached-ris')}
                  type='button'
                >
                  <Trans>Memcached RIs</Trans>
                </button>
              </li>
            </ul>

            <div className='mt-3'>
              {activeTab === 'daily-cost' && (
                <div className='wider-chart-container'>
                  <ECharts
                    title={t`Daily Cost Comparison`}
                    chartInfoTitle='aws_elasticache_optimization_daily_cost_comparison'
                    url={elasticacheCostAnalysisChartOrganizationOptimizerAWSMasterOptimizationsPath(organizationId)}
                    params={{ master_optimization_id: masterOptimizationId }}
                  />
                </div>
              )}

              {activeTab === 'node-type' && (
                <div className='row'>
                  <div className='col-md-6'>
                    <ECharts
                      title={t`Current Node Types`}
                      chartInfoTitle='aws_elasticache_current_node_types'
                      url={elasticacheNodeTypeChartsOrganizationOptimizerAWSMasterOptimizationsPath(organizationId)}
                      params={{ master_optimization_id: masterOptimizationId }}
                    />
                  </div>
                  <div className='col-md-6'>
                    <ECharts
                      title={t`Optimized Node Types`}
                      chartInfoTitle='aws_elasticache_optimized_node_types'
                      url={elasticacheOptimizedNodeTypeChartsOrganizationOptimizerAWSMasterOptimizationsPath(
                        organizationId,
                      )}
                      params={{ master_optimization_id: masterOptimizationId }}
                    />
                  </div>
                </div>
              )}

              {activeTab === 'redis-ris' && (
                <ECharts
                  title={t`ElastiCache Redis Reserved Instances`}
                  chartInfoTitle='aws_elasticache_redis_reserved_instances'
                  url={elasticacheRedisRiChartOrganizationOptimizerAWSMasterOptimizationsPath(organizationId)}
                  params={{ master_optimization_id: masterOptimizationId }}
                />
              )}

              {activeTab === 'memcached-ris' && (
                <ECharts
                  title={t`ElastiCache Memcached Reserved Instances`}
                  chartInfoTitle='aws_elasticache_memcached_reserved_instances'
                  url={elasticacheMemcachedRiChartOrganizationOptimizerAWSMasterOptimizationsPath(organizationId)}
                  params={{ master_optimization_id: masterOptimizationId }}
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
